import React from "react";

import { BasicTooltip } from "./Tooltip";
import type { BasicTooltipProps } from "./Tooltip";
import { useCheckOverflow } from "./hooks/useCheckOverflow";

export enum OverflowDirection {
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

export interface OverflowDisplayTooltipProps extends BasicTooltipProps {
  overflowDirection?: OverflowDirection;
  lineClamp?: number;
  styles?: React.CSSProperties;
}

export const OverflowDisplayTooltip = ({
  content,
  children,
  variant = "black",
  side = "top",
  usePortal = true,
  delayDuration,
  className,
  ignoreOverflow = false,
  overflowDirection = OverflowDirection.HORIZONTAL,
  lineClamp = 1,
  shouldShow = true,
  styles,
}: OverflowDisplayTooltipProps) => {
  const { ref, isOverflowing } = useCheckOverflow({});

  const getOverflowClasses = () => {
    const baseClasses = "block overflow-hidden";

    switch (overflowDirection) {
      case OverflowDirection.HORIZONTAL:
        return `${baseClasses} truncate`;
      case OverflowDirection.VERTICAL:
        return `${baseClasses}`;
      default:
        return `${baseClasses} truncate`;
    }
  };

  const getOverflowStyles = (): React.CSSProperties => {
    if (overflowDirection === OverflowDirection.VERTICAL && lineClamp) {
      return {
        display: "-webkit-box",
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: "vertical",
        ...styles,
      };
    }
    return styles || {};
  };

  const isReactElement = React.isValidElement(children);

  const combinedStyles = getOverflowStyles();
  const overflowClasses = getOverflowClasses();

  const childWithRef = isReactElement
    ? React.cloneElement(children as React.ReactElement, {
        ref,
        className:
          `${(children as React.ReactElement).props.className || ""} ${className || ""} ${overflowClasses}`.trim(),
        style: {
          ...(children as React.ReactElement).props.style,
          ...combinedStyles,
        },
      })
    : React.createElement(
        "span",
        {
          ref,
          className: `${className || ""} ${overflowClasses}`.trim(),
          style: combinedStyles,
        },
        children,
      );

  return (
    <BasicTooltip
      content={content}
      variant={variant}
      side={side}
      usePortal={usePortal}
      delayDuration={delayDuration}
      shouldShow={(isOverflowing || ignoreOverflow) && shouldShow}
    >
      {childWithRef}
    </BasicTooltip>
  );
};
